import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';

import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { TagModule } from 'primeng/tag';
import { TableModule } from 'primeng/table';
import { DialogModule } from 'primeng/dialog';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';


import { TooltipModule } from 'primeng/tooltip';
import { IndexInsertComponent } from '../index-insert/index-insert.component';

import { IndexHistoryService, IndexHistoryItem, IndexHistoryItemDetailed } from './index-history.service';
import { TranslateService } from '../../../services/translate.service';
import { RefreshService } from '../../../services/refresh.service';

@Component({
	selector: 'app-index-history',
	standalone: true,
	imports: [CommonModule, TableModule, TagModule, DialogModule, ButtonModule, ToastModule, TooltipModule],
	templateUrl: './index-history.component.html',
	styleUrls: ['./index-history.component.scss'],
	providers: [DialogService]
})
export class IndexHistoryComponent implements OnInit {
	indexHistory: IndexHistoryItem[] = [];
	loading = false;
	expandedRows: { [key: number]: boolean } = {};
	expansionDetails: { [key: number]: IndexHistoryItemDetailed[] } = {};
	expansionLoading: { [key: number]: boolean } = {};
	ref?: DynamicDialogRef;

	// Store assignment history per row
	assignmentHistory: { [key: number]: any[] } = {};
	assignmentLoading: { [key: number]: boolean } = {};
	activeExpansionTab: { [key: number]: 'source' | 'assignment' } = {};
	reloadLoading: { [key: number]: boolean } = {};

	constructor(
		private historyService: IndexHistoryService,
		public messageService: MessageService,
		private refreshService: RefreshService,
		public translateServ: TranslateService,
		public dialogService: DialogService,
	) { }

	ngOnInit() {
		this.loadTable();
	}

	getIndexTypeTags(indexTypeName: string | null | undefined): string[] {
		if (!indexTypeName) return [];

		return indexTypeName
			.split(/[,;|]+/)
			.map(indexName => indexName.trim())
			.filter(Boolean);
	}

	getIndexTagSeverity(indexName: string): 'info' | 'success' | 'warning' | 'danger' | 'secondary' | 'contrast' {
		const severities: Array<'info' | 'success' | 'warning' | 'danger' | 'secondary' | 'contrast'> = ['info', 'success', 'warning', 'danger', 'secondary', 'contrast'];
		const normalizedIndexName = indexName.toLowerCase();
		let colorIndex = 0;

		for (let charIndex = 0; charIndex < normalizedIndexName.length; charIndex++) {
			colorIndex += normalizedIndexName.charCodeAt(charIndex);
		}

		return severities[colorIndex % severities.length];
	}

	getTranslatedIndexType(indexName: string): string {
		return this.translateServ.dictionary?.[indexName] || indexName;
	}

	loadTable() {
		this.loading = true;
		this.historyService.getAllUploades().subscribe(data => {
			console.log('IndexHistory API returned:', data);
			this.indexHistory = data;
			console.log('IndexHistory filled variable:', this.indexHistory);
			this.loading = false;
		});
	}

	toggleRow(row: IndexHistoryItem) {
		const rowId = row.id;
		if (this.expandedRows[rowId]) {
			this.expandedRows[rowId] = false;
		} else {
			this.expandedRows[rowId] = true;
			if (!this.expansionDetails[rowId]) {
				this.expansionLoading[rowId] = true;
				this.historyService.getUploadDetails(row.importId).subscribe(details => {
					console.log('Expansion details for row', rowId, ':', details);
					this.expansionDetails[rowId] = details;
					this.expansionLoading[rowId] = false;
				});
			}
		}
	}

	// Open Excel preview dialog for a detail row

	downloadFile(detail: IndexHistoryItemDetailed) {
		if (detail.url) {
			const link = document.createElement('a');
			link.href = detail.url;
			link.download = detail.fileName || 'file.xlsx';
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
			this.messageService.add({ severity: 'success', summary: 'Download', detail: 'File was downloaded.' });
		}
	}

	// Open Finish Upload dialog and pass file names using dynamic dialog
	openFinishUploadDialog(row: IndexHistoryItem, details: IndexHistoryItemDetailed[]) {
		if (+row.code === 100 && details && details.length === 3) {
			const preloadedFileNames = details.map(d => d.fileName);
			const importId = row.importId;
			const fileSize = details.map(d => d.fileSize);
			console.warn('!!! preloadedFileNames:', preloadedFileNames, 'importId:', importId, 'fileSize:', fileSize);
			this.ref = this.dialogService.open(IndexInsertComponent, {
				header: this.translateServ.dictionary['FIN_UPL'] || 'Finish Upload',
				width: '70vw',
				contentStyle: { overflow: 'auto' },
				baseZIndex: 10000,
				maximizable: true,
				closable: true,
				data: {
					preloadedFileNames,
					importId,
					fileSize
				}
			});
			// Handle dialog close event
			this.ref.onClose.subscribe((result) => {
				this.refreshService.triggerPageRefresh();
			});
		}
	}

	onFinishUploadDialogHide() {
		if (this.ref) {
			this.ref.close();
			this.ref = undefined;
		}
	}

	public getNoteFile(importId: number): void {
		this.historyService.getNotesFileUrl(importId).subscribe(url => {
			if (url) {
				const link = document.createElement('a');
				link.href = url;
				link.download = importId + '.xlsx';
				document.body.appendChild(link);
				link.click();
				document.body.removeChild(link);
				this.messageService.add({ severity: 'success', summary: 'Download', detail: 'Notes file was downloaded.' });
			}
			else {
				this.messageService.add({ severity: 'error', summary: 'Download', detail: 'Notes file not found.' });
			}
		});
	}

	public getAssignmentHistory(importId: number, rowId: number): void {
		this.assignmentLoading[rowId] = true;
		this.historyService.getAssignmentHistory(importId).subscribe(data => {
			this.assignmentHistory[rowId] = data;
			this.assignmentLoading[rowId] = false;
		}, err => {
			this.assignmentHistory[rowId] = [];
			this.assignmentLoading[rowId] = false;
		});
	}

	public reloadIndex(row: IndexHistoryItem): void {
		this.reloadLoading[row.id] = true;
		this.historyService.reloadIndexImport(row.importId).subscribe({
			next: () => {
				this.reloadLoading[row.id] = false;
				this.loadTable();
			},
			error: () => {
				this.reloadLoading[row.id] = false;
				this.messageService.add({ severity: 'error', summary: 'Reload', detail: 'Failed to reload index.' });
			}
		});
	}
}
